import * as BABYLON from '@babylonjs/core';
import { GLTF2Export } from '@babylonjs/serializers';
import { ConfigurationError } from '../../../store/actions/configuration';
import { ModelRequest } from '../../ar';
import { ParticleAttachment, ParticleTreeNode } from './particleNodes';

export enum ViewerTransformType {
    PARTICLE_TRANSFORM = 'particleTransform',
    PARTICLE_DELTA_ORIGIN_TRANSFORM = 'particleDeltaOriginTransform',
    ROOT_CONFIGURATOR_TRANSFORM = 'rootConfiguratorTransform',
}

type ParticleTransformMetaData = {
    treeNode: ParticleTreeNode;
    transformHash: string;
};

/**
 * Generate a unique hash for a given particle tree node, using the unique particle id and the position / rotation.
 * This means a specific instance of an attachment can be compared against another tree directly.
 * @param treeNode the particle node to read from
 * @returns string SHA-256 hash
 */
const generateTransformHash = async (treeNode: ParticleTreeNode) => {
    const hashString =
        treeNode.particleId +
        JSON.stringify(treeNode.position) +
        JSON.stringify(treeNode.orientation);
    const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(hashString));
    return Array.prototype.map
        .call(new Uint8Array(hashBuffer), x => ('00' + x.toString(16)).slice(-2))
        .join('');
};

/**
 * Construct an interactive hotspot for dictating user choices
 * @param apUtilLayer the util layer to draw on (so it's always drawn on top)
 * @param parentTransform the transform position to use to attach to
 * @param attachmentKey the property of the attachments store as an id
 * @param attachment the full set of attachment points for this selection
 */
const addHotspot = (
    apUtilLayer: BABYLON.UtilityLayerRenderer,
    parentTransform: BABYLON.TransformNode,
    attachmentKey: string,
    attachment: ParticleAttachment,
) => {
    const actionManager = new BABYLON.ActionManager();

    attachment.attached.forEach(childAttachment => {
        // create the animated sphere, this is shown in the UI
        const apSphere = BABYLON.CreateSphere(
            'attachmentPoint',
            { diameter: 0.04 },
            apUtilLayer.utilityLayerScene,
        );
        const sphereMaterial = new BABYLON.StandardMaterial('hotspot');
        sphereMaterial.transparencyMode = 2;
        sphereMaterial.alpha = 0.1;

        const color = new BABYLON.Color3(1, 1, 1);
        sphereMaterial.emissiveColor = color;

        apSphere.material = sphereMaterial;
        apSphere.parent = parentTransform;
        let { x: px, y: py, z: pz } = childAttachment.position;
        apSphere.position = new BABYLON.Vector3(px, py, pz);

        apSphere.outlineColor = BABYLON.Color3.Black();
        apSphere.outlineWidth = 0.005;
        apSphere.renderOutline = true;

        // create a selection sphere, this is used entirely for click interaction.
        // it's larger than the gui one to aid touch interaction
        const selectionSphere = BABYLON.CreateSphere(
            'attachmentPointSelectionSphere',
            { diameter: 0.1 },
            apUtilLayer.utilityLayerScene,
        );
        selectionSphere.visibility = 0;
        selectionSphere.id = `${attachmentKey}:${attachment.name}`;
        selectionSphere.parent = apSphere;
        selectionSphere.actionManager = actionManager;

        // finally, create a copy of the sphere to animate a highlight on a second after creation
        const highlightAnimationSphere = BABYLON.CreateSphere(
            'attachmentPointHighlightAnimation',
            { diameter: 0.04 },
            apUtilLayer.utilityLayerScene,
        );
        highlightAnimationSphere.parent = apSphere;
        highlightAnimationSphere.material = sphereMaterial;

        const highlightAnimation = new BABYLON.Animation(
            'pulse-animation',
            'scaling',
            60,
            BABYLON.Animation.ANIMATIONTYPE_VECTOR3,
            BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT,
        );
        const keyFrames = [];
        keyFrames.push({
            frame: 0,
            value: new BABYLON.Vector3(1, 1, 1),
        });
        keyFrames.push({
            frame: 60,
            value: new BABYLON.Vector3(1, 1, 1),
        });
        keyFrames.push({
            frame: 90,
            value: new BABYLON.Vector3(3, 3, 3),
        });
        keyFrames.push({
            frame: 120,
            value: new BABYLON.Vector3(1, 1, 1),
        });
        highlightAnimation.setKeys(keyFrames);
        const easingFunction = new BABYLON.CircleEase();
        easingFunction.setEasingMode(BABYLON.EasingFunction.EASINGMODE_EASEOUT);
        highlightAnimation.setEasingFunction(easingFunction);

        highlightAnimationSphere.animations.push(highlightAnimation);
        const highlightAnimationContext = apUtilLayer.originalScene.beginAnimation(
            highlightAnimationSphere,
            0,
            120,
            false,
        );

        actionManager.registerAction(
            new BABYLON.CombineAction(BABYLON.ActionManager.OnPointerOverTrigger, [
                new BABYLON.InterpolateValueAction(
                    BABYLON.ActionManager.OnPointerOverTrigger,
                    sphereMaterial,
                    'alpha',
                    0.5,
                    100,
                    undefined,
                    true,
                ),
                new BABYLON.InterpolateValueAction(
                    BABYLON.ActionManager.OnPointerOverTrigger,
                    apSphere,
                    'scaling',
                    new BABYLON.Vector3(1.5, 1.5, 1.5),
                    100,
                    undefined,
                    true,
                ),
                new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPointerOverTrigger, () => {
                    highlightAnimationContext.stop();
                    highlightAnimationSphere.scaling = BABYLON.Vector3.One();
                }),
            ]),
        );

        actionManager.registerAction(
            new BABYLON.CombineAction(BABYLON.ActionManager.OnPointerOutTrigger, [
                new BABYLON.InterpolateValueAction(
                    BABYLON.ActionManager.OnPointerOutTrigger,
                    sphereMaterial,
                    'alpha',
                    0.2,
                    500,
                    undefined,
                    true,
                ),
                new BABYLON.InterpolateValueAction(
                    BABYLON.ActionManager.OnPointerOutTrigger,
                    apSphere,
                    'scaling',
                    new BABYLON.Vector3(1, 1, 1),
                    500,
                    undefined,
                    true,
                ),
            ]),
        );
    });
};

/**
 * Simple function for kicking off an animation when displaying a particle model in the scene
 * @param scene Babylon scene to trigger the animations with
 * @param transform node to animate
 */
const animateStartingParticlePosition = async (
    scene: BABYLON.Scene,
    transform: BABYLON.TransformNode,
): Promise<void> => {
    transform.setEnabled(true);
    const childTransforms = transform.getChildTransformNodes(
        true,
        node => node.metadata?.transformHash,
    );
    childTransforms.forEach(transform => transform.setEnabled(false));

    const instantiateAnimation = new BABYLON.Animation(
        'instantiate',
        'scaling',
        60,
        BABYLON.Animation.ANIMATIONTYPE_VECTOR3,
        BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT,
    );

    instantiateAnimation.setKeys([
        {
            frame: 0,
            value: BABYLON.Vector3.Zero(),
        },
        {
            frame: 15,
            value: BABYLON.Vector3.One(),
        },
    ]);

    const easingFunction = new BABYLON.BounceEase(2, 5);
    easingFunction.setEasingMode(BABYLON.EasingFunction.EASINGMODE_EASEOUT);
    instantiateAnimation.setEasingFunction(easingFunction);

    transform.animations.push(instantiateAnimation);
    const animation = scene.beginAnimation(transform, 0, 15);
    animation.disposeOnEnd = true;

    // play the animation then recursively animate children
    await animation.waitAsync();
    await Promise.all(
        childTransforms.map(transform => animateStartingParticlePosition(scene, transform)),
    );

    // clear all disposed animations on completion
    transform.animations = [];
};

/**
 * Add a model to the scene
 * @param treeNode The tree node to add to the scene (model url etc)
 * @param parentTransform The transform to attach the node to, required - the starting node should have a root furniture transform here
 * @returns Promise<BABYLON.TransformNode> the newly created transform which holds both the offset (delta) transform and the meshes
 */
const addParticleModel = async (
    shadowGenerator: BABYLON.ShadowGenerator,
    treeNode: ParticleTreeNode,
    parentTransform: BABYLON.TransformNode,
    dispatchError: (errPayload: {
        reason: string;
        type: ConfigurationError;
        particleId?: string;
        modelUrl?: string;
    }) => void,
): Promise<BABYLON.TransformNode> => {
    // create a root node and a delta origin node for offset position + rotation
    const particleTransform = new BABYLON.TransformNode(ViewerTransformType.PARTICLE_TRANSFORM);
    particleTransform.id = treeNode.particleId;

    const deltaOriginTransform = new BABYLON.TransformNode(
        ViewerTransformType.PARTICLE_DELTA_ORIGIN_TRANSFORM,
    );
    deltaOriginTransform.parent = particleTransform;

    // attach the meshes to the delta origin node
    try {
        const particle = await BABYLON.SceneLoader.ImportMeshAsync(null, treeNode.modelUrl);
        particle.meshes.forEach(mesh => {
            mesh.parent = deltaOriginTransform;
            shadowGenerator.addShadowCaster(mesh, true);
            mesh.receiveShadows = true;
        });
    } catch {
        dispatchError({
            reason: `failed to fetch 3d model`,
            type: 'renderer',
            particleId: treeNode.particleId,
            modelUrl: treeNode.modelUrl
        });
    }

    // parent the new particle transform if it's still valid (async operation to fetch, may have been removed!)
    if (parentTransform && !parentTransform.isDisposed()) {
        particleTransform.parent = parentTransform;
    } else {
        particleTransform.dispose();
        return particleTransform;
    }

    // set root node to raw position from attachment point
    let { x: px, y: py, z: pz } = treeNode.position;
    let { w: rw, x: rx, y: ry, z: rz } = treeNode.orientation;
    particleTransform.position = new BABYLON.Vector3(px, py, pz);
    particleTransform.rotationQuaternion = new BABYLON.Quaternion(rx, ry, rz, rw);

    // set delta origin to that from attached model
    let { x: opx, y: opy, z: opz } = treeNode.originDelta.position;
    let { w: orw, x: orx, y: ory, z: orz } = treeNode.originDelta.orientation;
    deltaOriginTransform.position = new BABYLON.Vector3(opx, opy, opz);
    deltaOriginTransform.rotationQuaternion = new BABYLON.Quaternion(orx, ory, orz, orw);

    // return root node
    return particleTransform;
};

/**
 * Recurse the particle tree adding furniture to the scene by the established attachment point, respecting user choices
 * @param scene Babylon scene to add to
 * @param shadowGenerator used to set the 3d models in the shadow gen system
 * @param treeNode the root node to begin adding from
 * @param apUtilLayer layer for drawing options hotspots to
 * @param parentTransform the starting transform for the node - this should be created beforehand as a master "Furniture" transform
 * @returns BABYLON.TransformNode representing the current node particle (not the initial furniture transform it's attached to)
 */
const addParticleTreeRecursive = async (
    scene: BABYLON.Scene,
    shadowGenerator: BABYLON.ShadowGenerator,
    treeNode: ParticleTreeNode,
    apUtilLayer: BABYLON.UtilityLayerRenderer,
    parentTransform: BABYLON.TransformNode,
    dispatchError: (errPayload: {
        reason: string;
        type: ConfigurationError;
        productId?: string;
    }) => void,
): Promise<BABYLON.TransformNode> => {
    const transform = await addParticleModel(
        shadowGenerator,
        treeNode,
        parentTransform,
        dispatchError,
    );
    await Promise.all(
        treeNode.attachments.active.flatMap(attached =>
            addParticleTreeRecursive(
                scene,
                shadowGenerator,
                attached,
                apUtilLayer,
                transform,
                dispatchError,
            ),
        ),
    );

    Object.entries(treeNode.attachments.options).forEach(([key, val]) =>
        val.forEach(v => {
            addHotspot(apUtilLayer, transform, key, v);
        }),
    );

    const transformHash = await generateTransformHash(treeNode);
    const metadata: ParticleTransformMetaData = { treeNode, transformHash };
    transform.metadata = metadata;

    return transform;
};

/**
 * Generate a list of child particle transform nodes which do not exist in the old structure.
 * This stops when it encounters a node which doesn't exist in the previous structure as that node already includes its' children.
 * @param newTransform new node from a furniture tree transform structure
 * @param oldTransform old node from a furniture tree transform structure
 * @returns flat array of new nodes which aren't in the previous structure
 */
const getNewNodes = (
    newTransform: BABYLON.TransformNode,
    oldTransform?: BABYLON.TransformNode,
): BABYLON.TransformNode[] => {
    // if there's no previous transform then there's nothing to remove, only add
    if (!oldTransform) return [newTransform];

    // create node hash lookups for convenience
    const oldChildTransforms = oldTransform.getChildTransformNodes(
        true,
        node => node.metadata?.transformHash,
    );
    const newChildTransforms = newTransform.getChildTransformNodes(
        true,
        node => node.metadata?.transformHash,
    );

    const oldChildTransformHashes = oldChildTransforms.map(node => node.metadata.transformHash);

    // identify which new children do not exist in the old node
    const addedChildren = newChildTransforms.filter(
        newNode => !oldChildTransformHashes.includes(newNode.metadata.transformHash),
    );

    // for each child which is identical across both nodes, recurse and append
    return newChildTransforms
        .filter(newNode => oldChildTransformHashes.includes(newNode.metadata.transformHash))
        .map(newNode => ({
            newNode,
            oldNode: oldChildTransforms.find(
                oldNode => oldNode.metadata?.transformHash === newNode.metadata.transformHash,
            ),
        }))
        .map(({ oldNode, newNode }) => getNewNodes(newNode, oldNode))
        .reduce((acc, cur) => acc.concat(cur), addedChildren);
};

/**
 * Given a particle tree node, log each particle id and grouped / indented attached particles
 * @param treeNode root node to log
 */
const logParticleTree = (treeNode: ParticleTreeNode) => {
    // Particle tree logging bypasses logging framework as it's
    // controlled by a dedicated flag
    console.log('[Renderer] New Furniture received', treeNode);
    const logRecursive = (treeNode: ParticleTreeNode) => {
        const hasAttached = treeNode.attachments.active.length > 0;
        const attachedNodes = hasAttached
            ? `, ${treeNode.attachments.active.length} attached nodes:`
            : '';
        const logString = `${treeNode.attachmentTag}: ${treeNode.particleId}${attachedNodes}`;
        hasAttached ? console.group(logString) : console.log(logString);
        if (treeNode.attachments.active.length) {
            treeNode.attachments.active.flatMap(attached => logRecursive(attached));
        }
        hasAttached && console.groupEnd();
    };
    logRecursive(treeNode);
};

/**
 * Given a list of particles and user options, update the furniture in the scene
 * @param scene the Babylon scene to use
 * @param tree the root node of the furniture particle tree
 * @param apUtilLayer utility layer used to draw hotspots to (always draws on top)
 * @param logFurniture if enabled, will log the tree structure of particle ids
 * @return Promise which resolves when the model data has been downloaded and returns a nested promise
 *  object (animationComplete) which resolves when the viewer has finished animating the models.
 */
export const updateViewer = async (
    scene: BABYLON.Scene,
    shadowGenerator: BABYLON.ShadowGenerator,
    apUtilLayer: BABYLON.UtilityLayerRenderer,
    tree: ParticleTreeNode,
    logFurniture: boolean,
    shadowSurfaces: { ground: BABYLON.Mesh | null; wall: BABYLON.Mesh | null },
    dispatchError: (errPayload: {
        reason: string;
        type: ConfigurationError;
        productId?: string;
    }) => void,
): Promise<{ animationComplete: Promise<void[]> }> => {
    const oldTransform = scene.transformNodes.find(
        transform => transform.name === ViewerTransformType.ROOT_CONFIGURATOR_TRANSFORM,
    );
    const oldHotSpots = [...apUtilLayer.utilityLayerScene.meshes];

    const allOldTransforms = scene.transformNodes.filter(
        transform => transform.name === ViewerTransformType.ROOT_CONFIGURATOR_TRANSFORM,
    );

    // log the tree structure
    logFurniture && logParticleTree(tree);

    try {
        // create new furniture transform and hide it
        const newTransform = new BABYLON.TransformNode(
            ViewerTransformType.ROOT_CONFIGURATOR_TRANSFORM,
        );
        newTransform.setEnabled(false);

        newTransform.position = new BABYLON.Vector3(0.0, 0.2, 0.0);

        await addParticleTreeRecursive(
            scene,
            shadowGenerator,
            tree,
            apUtilLayer,
            newTransform,
            dispatchError,
        );

        const back = newTransform.getHierarchyBoundingVectors().min.z;
        if (shadowSurfaces.ground)
            shadowSurfaces.ground.position = new BABYLON.Vector3(0, 0, 10 + back);
        if (shadowSurfaces.wall) shadowSurfaces.wall.position = new BABYLON.Vector3(0, 10, back);

        // identify the new nodes and delete the old furniture
        const newNodes = getNewNodes(newTransform, oldTransform);
        allOldTransforms.forEach(transform => transform.dispose(false, true));

        // clear old hotspots to ensure they don't get created during a clear (async op)
        oldHotSpots.forEach(hotspot => hotspot.dispose(false, true));

        // show the new furniture and animate in the new nodes
        newTransform.setEnabled(true);
        const animationComplete = Promise.all(
            newNodes.map(transform => animateStartingParticlePosition(scene, transform)),
        );
        return { animationComplete };
    } catch (err) {
        dispatchError({
            reason: `failed to add furniture`,
            type: 'renderer',
        });
        return { animationComplete: Promise.resolve([]) };
    }
};

/**
 * Generate and return a gltf binary (glb) Blob
 * @param scene used to construct the glb - the returned glb will only include nodes as part of the
 *      ViewerTransformType.ROOT_CONFIGURATOR_TRANSFORM tree (i.e. the configured object in the scene)
 * @param download if set will automatically trigger a browser download (default false)
 * @param filename if set will assign a name to the downloaded object
 * @returns Promise<Blob>
 */
export const generateGltf = async (
    scene: BABYLON.Scene,
    download = false,
    filename?: string,
): Promise<Blob> => {
    await import('@babylonjs/serializers');

    /** Return true if the provided node IS a ROOT_CONFIGURATOR_TRANSFORM or any of its parents are (recursively) */
    const shouldExportNode = (node: BABYLON.Node): boolean => {
        return (
            node.name === ViewerTransformType.ROOT_CONFIGURATOR_TRANSFORM ||
            (node.parent !== undefined &&
                node.parent !== null &&
                (node.parent.name === ViewerTransformType.ROOT_CONFIGURATOR_TRANSFORM ||
                    shouldExportNode(node.parent)))
        );
    };

    filename = filename ?? 'Configuration';
    const glb = await GLTF2Export.GLBAsync(scene, filename, {
        shouldExportNode,
        animationSampleRate: 0,
    });

    download && glb.downloadFiles();

    return glb.glTFFiles[`${filename}.glb`] as Blob;
};

/**
 * Generate and return a ModelRequest document for use on our model generation service (to build and return a url to a single glb file).
 * We query for particle transform nodes which are the nodes which contain the delta origin and therefore the mesh transform.
 * The world transform is computed by decomposing the world matrix of the origin delta node.
 * @param scene used to query for objects of type ViewerTransformType.PARTICLE_TRANSFORM, which in turn is used to store the particle id
 *  from the node and the world transform from leaf PARTICLE_DELTA_ORIGIN_TRANSFORM nodes containing the mesh data at the mesh file origin
 * @returns ModelRequest document
 */
export const generateModelJson = (scene: BABYLON.Scene): ModelRequest => {
    return {
        models: scene.transformNodes
            // grab all particle transforms
            .filter(node => node.name === ViewerTransformType.PARTICLE_TRANSFORM)
            // grab the id from the particle transform node and all child nodes of type PARTICLE_DELTA_ORIGIN_TRANSFORM
            // (containing our leaf node transform data)
            .flatMap(node =>
                node
                    .getChildTransformNodes(true)
                    .filter(
                        deltaOriginNode =>
                            deltaOriginNode.name ===
                            ViewerTransformType.PARTICLE_DELTA_ORIGIN_TRANSFORM,
                    )
                    .map(deltaOriginNode => ({ particleId: node.id, transform: deltaOriginNode })),
            )
            // transform position and orientation to world (i.e. with respect to world origin 0,0,0 and identity quaternion)
            .map(node => {
                const position = BABYLON.Vector3.Zero();
                const orientation = BABYLON.Quaternion.Identity();
                node.transform
                    .getWorldMatrix()
                    .decompose(BABYLON.Vector3.One(), orientation, position);
                return { assetId: node.particleId, position, orientation };
            })
            // convert to ModelRequest
            .map(node => ({
                assetId: node.assetId,
                position: {
                    x: node.position.x,
                    y: node.position.y,
                    z: node.position.z,
                },
                orientation: {
                    w: node.orientation.w,
                    x: node.orientation.x,
                    y: node.orientation.y,
                    z: node.orientation.z,
                },
            })),
    };
};
